import WarningIcon from '@mui/icons-material/WarningRounded';
import { Paper, Button, Grid, Typography, darken, capitalize, alpha, Stack } from '@mui/material';
import { type JSX, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { DateRange } from '@mui/x-date-pickers-pro';
import { TZDate } from '@repo/tzdate';
import MultipleNumberInput from 'src/components/common/MultipleNumberInput/MultipleNumberInput';
import type { QuantityInputItem } from '@repo/widget-utils/booking/domains/shared/types';
import { useLocale } from '@repo/i18n';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { QuantityError } from '@repo/widget-utils/booking/bookingHelpers';
import ControlledInputVisibilitySection from '../../common/inputs/ControlledInputVisibilitySection';
import BookingWidgetCard from '../booking-widget-card/BookingWidgetCard';
import { ensurePixelValue } from 'src/components/utils/styleUtils';
import {
    ProductInstance,
    ProductOption,
    TicketOptionWithQuantity,
    TicketType,
    Timeslot,
    Translations,
} from '@repo/types';
import { CostSummary } from './cost-summary/CostSummary';
import { useBookingContext } from 'src/widgets/BookingContext';
import {
    setHasChosenDate,
    setSelectedProductOption,
    setSelectedTicketType,
    setSelectedTimeSlot,
    setSelectedProducts,
} from 'src/state/booking/bookingReducer';
import Calendar from 'src/components/common/calendar/Calendar';
import PackageCalendar from 'src/components/common/calendar/PackageCalendar';
import { Dropdown } from 'src/components/common/dropdown/Dropdown';
import { SummaryPriceLineData } from '../summary/summary-mapping';
import { BookingHeaderPriceProps } from '../booking-widget-card/booking-header/BookingHeader';

/**
 * Translates QuantityError to user-facing error message
 */
function translateError(error: QuantityError, t: Translations): string {
    if (error.error === 'capacity') {
        return t.number_of_travelers_exceeded.parsed(error.value);
    }

    if (error.error === 'minEntrants') {
        return t.too_few_travelers_quantity.parsed(error.value);
    }

    return '';
}

const DEFAULT_NUM_PRICES = 3;

export default function BookingBase(props: {
    title: string;
    toggleButton?: JSX.Element;
    productTitle: string;
    travelerQuantities: TicketOptionWithQuantity[];
    selectedDateRange?: DateRange<TZDate>;
    onSelectDateRange?: (newRange: DateRange<TZDate>) => void;
    availabilityData: ProductInstance[];
    numPrices?: number;
    leftButtonLabel?: string;
    rightButtonLabel: string;
    onClickLeftButton?(): void;
    onClickRightButton(): void;
    availabilitySearchPeriod: { startDay: TZDate | null; endDay: TZDate | null };
    setAvailabilitySearchPeriod(availabilitySearchPeriod: {
        startDay: TZDate | null;
        endDay: TZDate | null;
    }): void;
    hideLeftButton?: boolean;
    productCapacity?: number;
    disableFixedPosition?: boolean;
    addMarginRightToHeader?: boolean;
    expandArrowInside: boolean;
    dateRangeVariant?: 'days' | 'nights';
    minDate?: TZDate;
    isPackageCalendar?: boolean;
    setSelectedDate?: (value: TZDate | null) => void;
    selectedDate?: TZDate | null;
    positionOffscreen: boolean;
    ticketTypes?: TicketType[];
    productOptions?: ProductOption[];
    availableTimeslots?: Timeslot[];
    isLoadingAvailabilities: boolean;
    dateSelectionFirst?: boolean;
    summaryLines: SummaryPriceLineData[];
    quantityErrors: QuantityError[];
    totalErrors: QuantityError[];
    disableBookButton?: boolean;
    warningLabel?: string;
    quantityInputItems: QuantityInputItem[];
    onChangeQuantity: (id: string, value: number) => void;
    bookingHeaderPriceProps?: BookingHeaderPriceProps;
    disabledMessage?: string;
}): JSX.Element {
    const {
        productTitle,
        leftButtonLabel,
        onClickLeftButton,
        onClickRightButton,
        rightButtonLabel,
        title,
        toggleButton,
        hideLeftButton,
        disableFixedPosition,
        addMarginRightToHeader,
        expandArrowInside,
        numPrices = DEFAULT_NUM_PRICES,
        positionOffscreen,
        isLoadingAvailabilities,
    } = props;

    const { hasChosenDate, visible, onToggleVisible, boxRef } = useBookingContext();

    function handleRightClick() {
        onClickRightButton();
    }

    return (
        <BookingWidgetCard
            bookingCardRef={boxRef}
            visible={visible}
            onToggleVisible={onToggleVisible}
            title={title}
            toggleButton={toggleButton}
            rightButtonLabel={rightButtonLabel}
            onClickRightButton={handleRightClick}
            leftButtonLabel={leftButtonLabel}
            onClickLeftButton={onClickLeftButton}
            hideLeftButton={hideLeftButton}
            hideRightButton={false}
            disableFixedPosition={disableFixedPosition}
            addMarginRightToHeader={addMarginRightToHeader}
            expandArrowInside={expandArrowInside}
            disableBookButton={props.disableBookButton}
            positionOffscreen={positionOffscreen}
            bookingHeaderPriceProps={props.bookingHeaderPriceProps}
        >
            <BookingBaseForm
                {...props}
                totalErrors={props.totalErrors}
                isLoading={isLoadingAvailabilities}
                quantityInputItems={props.quantityInputItems}
                onChangeQuantity={props.onChangeQuantity}
                disabledMessage={props.disabledMessage}
            />

            <CostSummary
                header={productTitle}
                lines={props.summaryLines}
                ensureSpaceForNumberOfLines={numPrices}
            />

            <WarningMessage label={!hasChosenDate ? null : props.warningLabel} />
        </BookingWidgetCard>
    );
}

export function BookingBaseInline(props: {
    title: string;
    productTitle: string;
    travelerQuantities: TicketOptionWithQuantity[];
    availabilityData: ProductInstance[];
    numPrices?: number;
    rightButtonLabel: string;
    onClickRightButton(): void;
    availabilitySearchPeriod: { startDay: TZDate | null; endDay: TZDate | null };
    setAvailabilitySearchPeriod(availabilitySearchPeriod: {
        startDay: TZDate | null;
        endDay: TZDate | null;
    }): void;
    productCapacity?: number;
    onSelectDateRange?: (newRange: DateRange<TZDate>) => void;
    selectedDateRange?: DateRange<TZDate>;
    dateRangeVariant?: 'days' | 'nights';
    minDate?: TZDate;
    ticketTypes?: TicketType[];
    productOptions?: ProductOption[];
    selectedDate?: TZDate | null;
    isLoadingAvailabilities: boolean;
    dateSelectionFirst?: boolean;
    summaryLines: SummaryPriceLineData[];
    quantityErrors: QuantityError[];
    totalErrors: QuantityError[];
    disableBookButton?: boolean;
    warningLabel?: string;
    quantityInputItems: QuantityInputItem[];
    onChangeQuantity: (id: string, value: number) => void;
    disabledMessage?: string;
}): JSX.Element {
    const {
        productTitle,
        onClickRightButton,
        rightButtonLabel,
        numPrices = DEFAULT_NUM_PRICES,
        isLoadingAvailabilities,
    } = props;

    const { hasChosenDate } = useBookingContext();

    const customizations = useCustomizations();
    const theme = useTheme();

    return (
        <Grid component={'div'}>
            <Paper
                elevation={5}
                sx={{
                    backgroundColor: customizations.bookingWidgetColor,
                    color: customizations.bookingWidgetColorContrast,
                    borderRadius: `calc(${ensurePixelValue(theme.shape.borderRadius)} * 2)`,
                    padding: 3,
                    maxWidth: '640px',
                    margin: '0 auto',
                }}
            >
                <Stack gap={2}>
                    <>
                        <BookingBaseForm
                            {...props}
                            totalErrors={props.totalErrors}
                            isLoading={isLoadingAvailabilities}
                            quantityInputItems={props.quantityInputItems}
                            onChangeQuantity={props.onChangeQuantity}
                            disabledMessage={props.disabledMessage}
                        />
                        <CostSummary
                            header={productTitle}
                            lines={props.summaryLines}
                            ensureSpaceForNumberOfLines={numPrices}
                        />
                        <WarningMessage label={!hasChosenDate ? null : props.warningLabel} />
                        <Button
                            sx={{
                                flex: '0 0 auto',
                                margin: '0 auto',
                                display: 'block',
                                minWidth: '10rem',
                                backgroundColor:
                                    customizations.primaryButtonStyle === 'contained'
                                        ? customizations.bookingWidgetPrimaryColor
                                        : 'transparent',
                                color:
                                    customizations.primaryButtonStyle === 'contained'
                                        ? customizations.bookingWidgetPrimaryColorContrast
                                        : customizations.bookingWidgetPrimaryColor,
                                borderColor:
                                    customizations.primaryButtonStyle === 'contained'
                                        ? 'transparent'
                                        : customizations.bookingWidgetPrimaryColor,
                                '&:hover': {
                                    backgroundColor:
                                        customizations.primaryButtonStyle === 'contained'
                                            ? darken(customizations.bookingWidgetPrimaryColor, 0.2)
                                            : alpha(customizations.bookingWidgetPrimaryColor, 0.2),
                                    color:
                                        customizations.primaryButtonStyle === 'contained'
                                            ? darken(
                                                  customizations.bookingWidgetPrimaryColorContrast,
                                                  0.2,
                                              )
                                            : undefined,
                                },
                                '&:focus': {
                                    boxShadow: `0 0 0 2px ${customizations.bookingWidgetPrimaryColorContrast}, \
                                                    0 0 0 3px ${customizations.bookingWidgetPrimaryColor}`,
                                },
                                '&:disabled': {
                                    backgroundColor:
                                        customizations.primaryButtonStyle === 'contained'
                                            ? darken(customizations.bookingWidgetPrimaryColor, 0.5)
                                            : 'transparent',
                                    color:
                                        customizations.primaryButtonStyle === 'contained'
                                            ? customizations.bookingWidgetColor
                                            : customizations.bookingWidgetPrimaryColor,
                                    borderColor:
                                        customizations.primaryButtonStyle === 'contained'
                                            ? 'transparent'
                                            : darken(customizations.bookingWidgetPrimaryColor, 0.5),
                                },
                            }}
                            onClick={onClickRightButton}
                            aria-label={rightButtonLabel}
                            variant={customizations.primaryButtonStyle}
                            color="button"
                            disabled={props.disableBookButton}
                        >
                            {capitalize(rightButtonLabel)}
                        </Button>
                    </>
                </Stack>
            </Paper>
        </Grid>
    );
}

export function WarningMessage(props: { label?: string | null }) {
    const theme = useTheme();
    const customizations = useCustomizations();

    if (!props.label) return null;

    return (
        <Grid textAlign="center" display="flex" justifyContent="center">
            <WarningIcon
                sx={{
                    color: customizations.bookingWidgetColorContrast,
                }}
            ></WarningIcon>
            <Typography
                sx={{ color: customizations.bookingWidgetColorContrast }}
                ml={theme.spacing(1)}
            >
                {props.label}
            </Typography>
        </Grid>
    );
}
export function BookingBaseForm(props: {
    travelerQuantities: TicketOptionWithQuantity[];
    availabilityData: ProductInstance[];
    availabilitySearchPeriod: { startDay: TZDate | null; endDay: TZDate | null };
    setAvailabilitySearchPeriod(availabilitySearchPeriod: {
        startDay: TZDate | null;
        endDay: TZDate | null;
    }): void;
    productCapacity?: number;
    selectedDateRange?: DateRange<TZDate>;
    onSelectDateRange?: (newRange: DateRange<TZDate>) => void;
    dateRangeVariant?: 'days' | 'nights';
    minDate?: TZDate;
    isPackageCalendar?: boolean;
    selectedDate?: TZDate | null;
    setSelectedDate?: (value: TZDate | null) => void;
    ticketTypes?: TicketType[];
    productOptions?: ProductOption[];
    availableTimeslots?: Timeslot[];
    dateSelectionFirst?: boolean;
    totalErrors: QuantityError[];
    backgroundColor?: string;
    color?: string;
    labelColor?: string;
    variant?: 'filled' | 'outlined';
    id?: string;
    inputFieldBorder?: string;
    isLoading?: boolean;
    quantityInputItems: QuantityInputItem[];
    onChangeQuantity: (id: string, value: number) => void;
    disabledMessage?: string;
}) {
    const ticketSection = (
        <TicketSection
            ticketTypes={props.ticketTypes}
            productOptions={props.productOptions}
            totalErrors={props.totalErrors}
            productCapacity={props.productCapacity}
            backgroundColor={props.backgroundColor}
            color={props.color}
            labelColor={props.labelColor}
            variant={props.variant}
            inputFieldBorder={props.inputFieldBorder}
            quantityInputItems={props.quantityInputItems}
            onChangeQuantity={props.onChangeQuantity}
            disabledMessage={props.disabledMessage}
        ></TicketSection>
    );

    const calendarSection = (
        <CalendarSection
            travelerQuantities={props.travelerQuantities}
            availabilityData={props.availabilityData}
            availabilitySearchPeriod={props.availabilitySearchPeriod}
            setAvailabilitySearchPeriod={props.setAvailabilitySearchPeriod}
            selectedDateRange={props.selectedDateRange}
            onSelectDateRange={props.onSelectDateRange}
            dateRangeVariant={props.dateRangeVariant}
            minDate={props.minDate}
            isPackageCalendar={props.isPackageCalendar}
            selectedDate={props.selectedDate}
            setSelectedDate={props.setSelectedDate}
            id={props.id}
            backgroundColor={props.backgroundColor}
            color={props.color}
            labelColor={props.labelColor}
            variant={props.variant}
            availableTimeslots={props.availableTimeslots}
            inputFieldBorder={props.inputFieldBorder}
            isLoading={props.isLoading}
        ></CalendarSection>
    );

    const orderedSections = props.dateSelectionFirst
        ? [calendarSection, ticketSection]
        : [ticketSection, calendarSection];

    return <ControlledInputVisibilitySection>{orderedSections}</ControlledInputVisibilitySection>;
}

function TicketTypeSelection(props: { ticketTypes: TicketType[] }) {
    const { t } = useLocale();
    const customizations = useCustomizations();
    const { selectedTicketType, bookingDispatch, attemptedBooking } = useBookingContext();

    return (
        <Dropdown
            value={selectedTicketType ?? null}
            options={props.ticketTypes}
            getOptionLabel={(opt) => opt?.name ?? t.select_price_type}
            onChange={(opt) => {
                bookingDispatch(setSelectedTicketType(opt ?? undefined));
            }}
            labelColor={customizations.bookingWidgetColorContrast}
            label={t.select_price_type}
            error={attemptedBooking && !selectedTicketType ? t.please_select_price_type : undefined}
        />
    );
}

/**
 * Dropdown for selecting a product option (e.g., "Morning Tour" vs "Afternoon Tour").
 * Only renders when product has 2+ options. First option is auto-selected by parent.
 */
function ProductOptionSelection(props: { productOptions: ProductOption[] }) {
    const { t } = useLocale();
    const customizations = useCustomizations();
    const { selectedProductOption, bookingDispatch } = useBookingContext();

    return (
        <Dropdown
            value={selectedProductOption ?? null}
            options={props.productOptions}
            getOptionLabel={(opt) => opt?.title ?? ''}
            onChange={(opt) => {
                bookingDispatch(setSelectedProductOption(opt ?? undefined));
            }}
            labelColor={customizations.bookingWidgetColorContrast}
            label={t.tour_option}
        />
    );
}

function TicketSelection(props: {
    items: QuantityInputItem[];
    onChangeQuantity: (id: string, value: number) => void;
    totalErrors: QuantityError[];
    productCapacity?: number;
    backgroundColor?: string;
    color?: string;
    labelColor?: string;
    variant?: 'filled' | 'outlined';
    inputFieldBorder?: string;
    disabledMessage?: string;
}) {
    const { t } = useLocale();
    const customizations = useCustomizations();

    // Translate first error to global error message
    const globalError = props.totalErrors[0] ? translateError(props.totalErrors[0], t) : undefined;

    return (
        <MultipleNumberInput
            items={props.items}
            onChange={props.onChangeQuantity}
            globalError={globalError}
            labelColor={props.labelColor ?? customizations.bookingWidgetColorContrast}
            backgroundColor={props.backgroundColor}
            color={props.color}
            variant={props.variant}
            border={props.inputFieldBorder}
            disabledMessage={props.disabledMessage}
        />
    );
}

function TicketSection(props: {
    ticketTypes?: TicketType[];
    productOptions?: ProductOption[];
    productCapacity?: number;
    totalErrors: QuantityError[];
    backgroundColor?: string;
    color?: string;
    labelColor?: string;
    variant?: 'filled' | 'outlined';
    inputFieldBorder?: string;
    quantityInputItems: QuantityInputItem[];
    onChangeQuantity: (id: string, value: number) => void;
    disabledMessage?: string;
}) {
    const { selectedProductOption } = useBookingContext();

    // Filter ticket options by selected product option (OCTO only)
    const filteredItems = useMemo(() => {
        // No filtering if no product options or no selection
        if (!props.productOptions?.length || !selectedProductOption) {
            return props.quantityInputItems;
        }
        return props.quantityInputItems.filter(
            (item) => item.productOptionId === selectedProductOption.id,
        );
    }, [props.quantityInputItems, selectedProductOption, props.productOptions]);

    return (
        <>
            {/* Product option dropdown - only if 2+ options */}
            {props.productOptions && props.productOptions.length > 1 && (
                <ProductOptionSelection productOptions={props.productOptions} />
            )}
            {/* Price-type dropdown - only if 2+ types. With a single type the
                effect in Booking.tsx auto-selects it, so the dropdown would
                just be a non-interactive label. */}
            {props.ticketTypes && props.ticketTypes.length > 1 && (
                <TicketTypeSelection ticketTypes={props.ticketTypes}></TicketTypeSelection>
            )}
            <TicketSelection
                items={filteredItems}
                onChangeQuantity={props.onChangeQuantity}
                totalErrors={props.totalErrors}
                productCapacity={props.productCapacity}
                backgroundColor={props.backgroundColor}
                color={props.color}
                labelColor={props.labelColor}
                variant={props.variant}
                inputFieldBorder={props.inputFieldBorder}
                disabledMessage={props.disabledMessage}
            />
        </>
    );
}

function CalendarSection(props: {
    travelerQuantities: TicketOptionWithQuantity[];
    selectedDateRange?: DateRange<TZDate>;
    onSelectDateRange?: (newRange: DateRange<TZDate>) => void;
    availabilityData: ProductInstance[];
    availabilitySearchPeriod: { startDay: TZDate | null; endDay: TZDate | null };
    setAvailabilitySearchPeriod(availabilitySearchPeriod: {
        startDay: TZDate | null;
        endDay: TZDate | null;
    }): void;
    dateRangeVariant?: 'days' | 'nights';
    minDate?: TZDate;
    isPackageCalendar?: boolean;
    setSelectedDate?: (value: TZDate | null) => void;
    selectedDate?: TZDate | null;
    availableTimeslots?: Timeslot[];
    backgroundColor?: string;
    color?: string;
    labelColor?: string;
    variant?: 'filled' | 'outlined';
    id?: string;
    inputFieldBorder?: string;
    isLoading?: boolean;
}) {
    const {
        travelerQuantities,
        availabilityData,
        availabilitySearchPeriod,
        setAvailabilitySearchPeriod,
        selectedDateRange,
        onSelectDateRange,
        dateRangeVariant,
        minDate,
        isPackageCalendar,
        selectedDate,
        setSelectedDate,
        id,
        backgroundColor,
        color,
        labelColor,
        variant,
        availableTimeslots,
        inputFieldBorder,
        isLoading,
    } = props;

    const { selectedTimeSlot, hasChosenDate, attemptedBooking, bookingDispatch } =
        useBookingContext();

    const isPackage = isPackageCalendar && setSelectedDate;

    return (
        <>
            {isPackage && (
                <PackageCalendar
                    hasChosenDate={hasChosenDate}
                    setHasChosenDate={(value) => {
                        bookingDispatch(setHasChosenDate(value));
                    }}
                    selectedDate={selectedDate ?? null}
                    setSelectedDate={setSelectedDate}
                    availabilityData={availabilityData}
                    setAvailabilitySearchPeriod={setAvailabilitySearchPeriod}
                    minDate={minDate}
                    attemptedBooking={attemptedBooking}
                    backgroundColor={backgroundColor}
                    color={color}
                    labelColor={labelColor}
                    variant={variant}
                    id={id}
                    border={inputFieldBorder}
                    isLoading={isLoading}
                />
            )}
            {!isPackage && (
                <Calendar
                    dateRangeVariant={dateRangeVariant}
                    onSelectDateRange={onSelectDateRange}
                    selectedDateRange={selectedDateRange}
                    selectedTimeSlot={selectedTimeSlot}
                    onSelectTimeSlot={(timeslot) => {
                        bookingDispatch(setSelectedTimeSlot(timeslot));
                    }}
                    setSelectedProducts={(products) => {
                        bookingDispatch(setSelectedProducts(products));
                    }}
                    availabilityData={availabilityData}
                    availabilitySearchPeriod={availabilitySearchPeriod}
                    setAvailabilitySearchPeriod={setAvailabilitySearchPeriod}
                    hasChosenDate={hasChosenDate}
                    setHasChosenDate={(value) => {
                        bookingDispatch(setHasChosenDate(value));
                    }}
                    attemptedBooking={attemptedBooking}
                    minDate={minDate}
                    id={id}
                    backgroundColor={backgroundColor}
                    color={color}
                    labelColor={labelColor}
                    variant={variant}
                    quantities={travelerQuantities}
                    availableTimeslots={availableTimeslots}
                    border={inputFieldBorder}
                    isLoading={isLoading}
                />
            )}
        </>
    );
}
